import { Component, EventEmitter, ViewChild, HostListener, AfterViewInit , CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA} from "@angular/core";
import { NgSelectComponent } from "../shared/ng-select/ng-select.component";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { FormGroup, Validators, FormControl } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { CdkDragDrop, moveItemInArray, DragDropModule } from "@angular/cdk/drag-drop";
import { EmitterService } from "../../services/emitter.service";
import { IDropDownView, IDropDownItemView } from "../../interfaces/idropdowns";
import { DropDownItemService } from "../../services/dropdownitem.service";
import { DropDownsService } from "../../services/dropdowns.service";
import { TrimmedMinLengthValidator } from "../../validators/trimmedMinLength.validator";
import { IButton } from "../../interfaces/ibuttons";
import { IPresentationType } from "../../interfaces/ipresentationType";
import { DropDownItem } from "../../models/dropdown";
import { ICanComponentDeactivate } from "../../interfaces/icancomponentdeactivate";
import { Observable } from "rxjs";
import { DropDownPresentationType } from "../../enums/dropdownpresentationtype";
import { ConfirmationModalComponent } from "../confirmationmodal/confirmationmodal.component";
import { HeaderComponent } from "../header/header.component";

/**
 * DropDown component
 */
@Component({
    selector: "dropdown",
    templateUrl: "./dropdown.component.html",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, NgSelectComponent, ConfirmationModalComponent, DragDropModule, HeaderComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    styles: [`
        .drag-item {
            cursor: pointer;
            transition: transform 0.2s ease;
        }
        
        .drag-item:active {
            cursor: pointer;
        }
        
        .d-flex[cdkDragHandle] {
            cursor: pointer;
        }
        
        .d-flex[cdkDragHandle]:active {
            cursor: pointer;
        }
        
        .drag-handle {
            cursor: pointer;
            padding: 4px;
            color: #000000;
            font-size: 16px;
            font-weight: 800;
        }
        
        .drag-handle:hover {
            color: #000000;
        }
        
        .drag-handle:active {
            cursor: pointer;
        }
        
        .cdk-drag-preview {
            box-sizing: border-box;
            border-radius: 4px;
            box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2),
                        0 8px 10px 1px rgba(0, 0, 0, 0.14),
                        0 3px 14px 2px rgba(0, 0, 0, 0.12);
            background: white;
            opacity: 0.9;
            transform: rotate(5deg);
        }
        
        .cdk-drag-placeholder {
            opacity: 0.4;
            background: #f8f9fa;
            border: 2px dashed #dee2e6;
        }
        
        .cdk-drag-animating {
            transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
        }
        
        .list-group.cdk-drop-list-dragging .drag-item:not(.cdk-drag-placeholder) {
            transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
        }
        
        .list-group-item:hover .drag-handle {
            color: #000000 !important;
        }
        
        .drag-handle i {
            font-weight: 400;
            transform: scaleX(0.9);
            color: #000000;
        }
        
        .list-group-item {
            padding: 3px 8px;
            line-height: 1.1;
            border: none !important;
        }
        
        .edit-btn {
            border: none !important;
            background: none !important;
            color: #000000 !important;
            font-size: 16px !important;
            font-weight: 700 !important;
            padding: 4px 8px !important;
        }
        
        .edit-btn:hover {
            color: #333333 !important;
            background: rgba(0,0,0,0.05) !important;
        }
        
        .edit-btn i {
            font-size: 16px !important;
        }
        
        .cdk-drop-list {
            display: block;
            min-height: 60px;
        }
        
        .list-group {
            border: none !important;
        }
        
        .list-group .list-group-item:first-child {
            border-top: none !important;
        }
        
        .list-group .list-group-item:last-child {
            border-bottom: none !important;
        }
        
        .cdk-drag {
            transition: transform 200ms ease;
        }
        
        .cdk-drag:hover {
            background-color: #f8f9fa;
        }
    `]
})
export class DropDownComponent implements ICanComponentDeactivate, AfterViewInit {
    dropDown: IDropDownView;
    dropDownForm: FormGroup;
    headerButtons: IButton[];
    title: string;
    presentationTypes: IPresentationType[];
    types: DropDownItem[];
    selectedPresentationType: DropDownItem = null;
    dropDownPresentationType: any = DropDownPresentationType;
    @ViewChild("confirmModal") confirmModal;
    originalData: string;
    constructor(private activatedRoute: ActivatedRoute,
        private router: Router,
        private dropDownItemService: DropDownItemService,
        private toastr: ToastrService,
        private dropDownService: DropDownsService) {
        this.presentationTypes = this.activatedRoute.snapshot.data["presentationType"];
        this.types = this.presentationTypes.map((t: IPresentationType) => new DropDownItem(t.id, t.description, null));
        if (this.activatedRoute.snapshot.data["dropDown"] != null) {
            this.dropDown = this.activatedRoute.snapshot.data["dropDown"] as IDropDownView;
            this.selectedPresentationType = this.types.find((i) => {
                return i.id === this.dropDown.presentationTypeId;
            });
        } else {
            this.selectedPresentationType = null;
        }

        this.initForm();

        const emitter: EventEmitter<any> = EmitterService.get("Create drop down item");
        const button: IButton = { label: "Create drop down item", btnClass: "fa-plus", btnClicked: emitter, disable: false };
        this.headerButtons = [button];

        emitter.subscribe(() => { this.addDropDownItemClicked(); });
        this.title = this.dropDown != null
            ? "Edit drop down"
            : "Create drop down";
    }

    ngAfterViewInit(): void {
        this.originalData = JSON.stringify(this.dropDownForm.value);
    }

    initForm(): void {
        // For ng-select, we need to set the initial value as the selected object, not just the ID
        let initialPresentationType = null;
        if (this.dropDown && this.dropDown.presentationTypeId) {
            initialPresentationType = this.types.find(t => t.id === this.dropDown.presentationTypeId) || null;
        }

        this.dropDownForm = new FormGroup({
            label: new FormControl(this.dropDown ? this.dropDown.label : null, [Validators.required, TrimmedMinLengthValidator(3), Validators.maxLength(100)]),
            description: new FormControl(this.dropDown ? this.dropDown.description : null, [
                Validators.required,
                TrimmedMinLengthValidator(3),
                Validators.maxLength(200)
            ]),
            allowMultiple: new FormControl({ value: this.dropDown ? !!this.dropDown.allowMultiple : false, disabled: !!(this.dropDown && this.dropDown.id)}, []),
            presentationType: new FormControl({ value: initialPresentationType, disabled: !!(this.dropDown && this.dropDown.id) }, [Validators.required]),
            helpText: new FormControl(this.dropDown ? this.dropDown.helpText : null, [Validators.maxLength(50)]),
            id: new FormControl(this.dropDown ? this.dropDown.id : null)
        });
    }


    presentationSelected(type: DropDownItem): void {
        this.selectedPresentationType = type;
    }

    onPresentationTypeChange(event: any): void {
        const selectedId = parseInt(event.target.value);
        if (selectedId && !isNaN(selectedId)) {
            this.selectedPresentationType = this.types.find(t => t.id === selectedId) || null;
        } else {
            this.selectedPresentationType = null;
        }
    }

    onNgSelectPresentationTypeChange(selectedItem: any): void {
        if (selectedItem && selectedItem.id) {
            this.selectedPresentationType = this.types.find(t => t.id === selectedItem.id) || null;
        } else {
            this.selectedPresentationType = null;
        }
    }

    saveClicked(form: IDropDownView, valid: boolean): void {
        // Handle ng-select value - it could be an object or ID
        const presentationTypeValue = this.dropDownForm.get('presentationType').value;
        if (presentationTypeValue && typeof presentationTypeValue === 'object') {
            form.presentationTypeId = presentationTypeValue.id;
        } else if (this.selectedPresentationType) {
            form.presentationTypeId = this.selectedPresentationType.id;
        } else {
            form.presentationTypeId = null;
        }
        
        form.helpText = form.helpText || "";
        if (valid) {
            if (form.id != null) {
                this.dropDownService.update(form).subscribe(() => {
                    this.toastr.success("Drop down updated successfully");
                    this.originalData = JSON.stringify(this.dropDownForm.value);
                });
            } else {
                this.dropDownService.create(form).subscribe(dd => {
                    this.dropDown = dd;
                    this.toastr.success("Drop down added successfully");
                    this.dropDownForm.get("id").setValue(dd.id);
                    this.dropDownForm.get("allowMultiple").disable();
                    this.originalData = JSON.stringify(this.dropDownForm.value);
                });
            }
        }
    }

    /**
     * Navigates to a particular drop down item based on the item selected
     * @param item The drop down item to navigate too
     */
    dropDownItemClicked(item: IDropDownItemView): void {
        this.router.navigate(["/dropdownitem", item.dropDownId, item.id]);
    }

    /**
     * Updates the sort order of the drop down items
     * @param item The drop down item being dragged and dropped
     */
    updateDropDownItemsSortOrder(item: IDropDownItemView): void {
        this.dropDownItemService.updateSortOrder(this.dropDown.items).subscribe(() => {
            this.toastr.success("Sort order of \"" + item.text + "\" has been successfully changed");
        });
    }

    /**
     * Handles drag and drop events to reorder items
     * @param event The CDK drop event
     */
    onDrop(event: CdkDragDrop<IDropDownItemView[]>): void {
        if (event.previousIndex !== event.currentIndex) {
            moveItemInArray(this.dropDown.items, event.previousIndex, event.currentIndex);
            
            // Update sort order values
            this.updateSortOrderValues();
            
            // Update the database with new sort order
            const movedItem = this.dropDown.items[event.currentIndex];
            this.updateDropDownItemsSortOrder(movedItem);
        }
    }

    /**
     * Updates the sort order property of all items based on their current array position
     */
    private updateSortOrderValues(): void {
        this.dropDown.items.forEach((item, index) => {
            item.sortOrder = index + 1;
        });
    }

    /**
     * Navigates to the drop down
     */
    addDropDownItemClicked(): void {
        this.router.navigate(["/dropdownitem", this.dropDown.id]);
    }

    /**
     * Navigates to the prepopulate drop down page
     */
    prepopulateClicked(): void {
        this.router.navigate(["/dropdownprepopulateitems", this.dropDown.id]);
    }

    @HostListener("window:beforeunload")
    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        let changedSet: string = JSON.stringify(this.dropDownForm.value);
        if (this.originalData !== changedSet) {
            this.confirmModal.openModal();
            return false;
        } else {
            return true;
        }
    }
    onAccept(): void {
        // reset the the original back to whats currenlty in itemConfigSet, this allows to pass check  in candeactivate
        this.originalData = JSON.stringify(this.dropDownForm.value);
        this.router.navigate([this.navigatingTo]);
        this.confirmModal.closeModal();
    }

    onDecline(): void {
        this.confirmModal.closeModal();
    }
    navigatingTo: string;
}


